// Meeting AI chat: wire types and parsers. The contract is memoru-service
// docs/api/meeting-ai-chat.md; tests/fixtures/chat holds its sample responses.
// Structure errors throw (the same as every other parser in src/api); an enum
// value this client does not know yields null or a safe default instead, so a
// newer server never breaks an older client.

import { isRecord } from "@/shared/api/envelope";

export const CHAT_MAX_QUESTION_CHARS = 2000;
/** The composer shows its counter from this length, as the contract recommends. */
export const CHAT_COUNTER_FROM = 1800;
export const CHAT_MAX_MESSAGES = 50;

export type ChatAvailabilityState = "available" | "meeting_live" | "no_transcript" | "too_long";

export interface ChatAvailability {
  state: ChatAvailabilityState;
  questions_used_24h: number;
  questions_limit_24h: number;
  /** When the next question is allowed; set only while a 24-hour limit is used up. */
  retry_at: string | null;
}

export interface Chat {
  id: string;
  meeting_id: string;
  title: string;
  message_count: number;
  last_message_preview: string;
  created_at: string;
  updated_at: string;
}

export interface ChatList {
  availability: ChatAvailability;
  chats: Chat[];
  next_cursor: string | null;
  has_more: boolean;
}

export interface ChatCitation {
  /** The number in the answer's `[[cite:N]]` marker. */
  index: number;
  segment_id: string;
  timestamp: string;
  offset_seconds: number;
  speaker: string;
  quote: string;
}

export type ChatRole = "user" | "assistant";
export type ChatMessageStatus = "streaming" | "complete" | "stopped" | "failed";
export type ChatGrounding = "grounded" | "unverified" | "not_in_meeting";
export type ChatFinishReason = "stop" | "length" | "stopped" | "error";
export type ChatFeedback = "up" | "down";

export interface ChatMessage {
  id: string;
  chat_id: string;
  role: ChatRole;
  status: ChatMessageStatus;
  content: string;
  prompt_id: string | null;
  citations: ChatCitation[];
  grounding: ChatGrounding | null;
  finish_reason: ChatFinishReason | null;
  transcript_stale: boolean;
  feedback: ChatFeedback | null;
  /** Present only on a failed answer. */
  error: { code: string; message: string } | null;
  created_at: string;
  completed_at: string | null;
}

export interface ChatMessagePage {
  items: ChatMessage[];
  next_cursor: string | null;
  has_more: boolean;
}

export interface ChatPrompt {
  id: string;
  label: string;
  description: string;
  /** A Lucide icon name. */
  icon: string;
}

export type ChatSendInput = { content: string } | { promptId: string };

export type ChatStreamPhase = "reading" | "writing";

/** One event of an answer stream, in the order the server sends them. */
export type ChatStreamEvent =
  | { type: "chat"; chat: Chat }
  | { type: "accepted"; userMessage: ChatMessage; assistantMessageId: string }
  | { type: "status"; phase: ChatStreamPhase }
  | { type: "delta"; text: string }
  | { type: "citation"; citation: ChatCitation }
  | { type: "done"; message: ChatMessage; availability: ChatAvailability }
  | { type: "error"; code: string; message: string; retryable: boolean; messageId: string };

const AVAILABILITY_STATES: readonly ChatAvailabilityState[] = ["available", "meeting_live", "no_transcript", "too_long"];
const STATUSES: readonly ChatMessageStatus[] = ["streaming", "complete", "stopped", "failed"];
const GROUNDINGS: readonly ChatGrounding[] = ["grounded", "unverified", "not_in_meeting"];
const FINISH_REASONS: readonly ChatFinishReason[] = ["stop", "length", "stopped", "error"];

function str(value: unknown, what: string): string {
  if (typeof value !== "string") throw new Error(`Invalid chat response: ${what}`);
  return value;
}

function num(value: unknown, what: string): number {
  if (typeof value !== "number" || !Number.isFinite(value)) throw new Error(`Invalid chat response: ${what}`);
  return value;
}

function nullableStr(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

function oneOf<T extends string>(value: unknown, allowed: readonly T[]): T | null {
  return typeof value === "string" && (allowed as readonly string[]).includes(value) ? (value as T) : null;
}

function record(value: unknown, what: string): Record<string, unknown> {
  if (!isRecord(value)) throw new Error(`Invalid chat response: ${what}`);
  return value;
}

export function parseChatAvailability(value: unknown): ChatAvailability {
  const v = record(value, "availability");
  return {
    // An unknown state is treated as unavailable rather than silently allowing a send.
    state: oneOf(v.state, AVAILABILITY_STATES) ?? "no_transcript",
    questions_used_24h: num(v.questions_used_24h, "questions_used_24h"),
    questions_limit_24h: num(v.questions_limit_24h, "questions_limit_24h"),
    retry_at: nullableStr(v.retry_at),
  };
}

export function parseChat(value: unknown): Chat {
  const v = record(value, "chat");
  return {
    id: str(v.id, "chat.id"),
    meeting_id: str(v.meeting_id, "chat.meeting_id"),
    title: str(v.title, "chat.title"),
    message_count: num(v.message_count, "chat.message_count"),
    last_message_preview: typeof v.last_message_preview === "string" ? v.last_message_preview : "",
    created_at: str(v.created_at, "chat.created_at"),
    updated_at: str(v.updated_at, "chat.updated_at"),
  };
}

export function parseChatList(value: unknown): ChatList {
  const v = record(value, "chat list");
  return {
    availability: parseChatAvailability(v.availability),
    chats: (Array.isArray(v.chats) ? v.chats : []).map(parseChat),
    next_cursor: nullableStr(v.next_cursor),
    has_more: v.has_more === true,
  };
}

export function parseChatCitation(value: unknown): ChatCitation {
  const v = record(value, "citation");
  return {
    index: num(v.index, "citation.index"),
    segment_id: str(v.segment_id, "citation.segment_id"),
    timestamp: str(v.timestamp, "citation.timestamp"),
    offset_seconds: num(v.offset_seconds, "citation.offset_seconds"),
    speaker: typeof v.speaker === "string" ? v.speaker : "",
    quote: typeof v.quote === "string" ? v.quote : "",
  };
}

export function parseChatMessage(value: unknown): ChatMessage {
  const v = record(value, "message");
  const role = v.role === "user" || v.role === "assistant" ? v.role : null;
  if (!role) throw new Error("Invalid chat response: message.role");
  const error = isRecord(v.error) && typeof v.error.code === "string"
    ? { code: v.error.code, message: typeof v.error.message === "string" ? v.error.message : "" }
    : null;
  return {
    id: str(v.id, "message.id"),
    chat_id: str(v.chat_id, "message.chat_id"),
    role,
    status: oneOf(v.status, STATUSES) ?? "complete",
    content: typeof v.content === "string" ? v.content : "",
    prompt_id: nullableStr(v.prompt_id),
    citations: (Array.isArray(v.citations) ? v.citations : []).map(parseChatCitation),
    grounding: oneOf(v.grounding, GROUNDINGS),
    finish_reason: oneOf(v.finish_reason, FINISH_REASONS),
    transcript_stale: v.transcript_stale === true,
    feedback: v.feedback === "up" || v.feedback === "down" ? v.feedback : null,
    error,
    created_at: str(v.created_at, "message.created_at"),
    completed_at: nullableStr(v.completed_at),
  };
}

export function parseChatMessagePage(value: unknown): ChatMessagePage {
  const v = record(value, "message page");
  return {
    items: (Array.isArray(v.items) ? v.items : []).map(parseChatMessage),
    next_cursor: nullableStr(v.next_cursor),
    has_more: v.has_more === true,
  };
}

export function parseChatPrompts(value: unknown): ChatPrompt[] {
  if (!Array.isArray(value)) throw new Error("Invalid chat response: prompts");
  return value.map((item) => {
    const v = record(item, "prompt");
    return {
      id: str(v.id, "prompt.id"),
      label: str(v.label, "prompt.label"),
      description: typeof v.description === "string" ? v.description : "",
      icon: typeof v.icon === "string" ? v.icon : "",
    };
  });
}

/**
 * Parses one server-sent event. Returns null for an event this client does not
 * know, so the stream keeps going when the server adds one.
 */
export function parseChatStreamEvent(name: string, data: string): ChatStreamEvent | null {
  let payload: unknown;
  try {
    payload = JSON.parse(data);
  } catch {
    throw new Error(`Invalid chat stream: ${name} is not JSON`);
  }
  const v = record(payload, name);
  switch (name) {
    case "chat":
      return { type: "chat", chat: parseChat(v.chat) };
    case "accepted":
      return { type: "accepted", userMessage: parseChatMessage(v.user_message), assistantMessageId: str(v.assistant_message_id, "assistant_message_id") };
    case "status": {
      const phase = v.phase === "writing" ? "writing" : "reading";
      return { type: "status", phase };
    }
    case "delta":
      return { type: "delta", text: str(v.text, "delta.text") };
    case "citation":
      return { type: "citation", citation: parseChatCitation(v.citation) };
    case "done":
      return { type: "done", message: parseChatMessage(v.message), availability: parseChatAvailability(v.availability) };
    case "error":
      return {
        type: "error",
        code: str(v.code, "error.code"),
        message: typeof v.message === "string" ? v.message : "",
        retryable: v.retryable === true,
        messageId: typeof v.message_id === "string" ? v.message_id : "",
      };
    default:
      return null;
  }
}
